import { useEffect, useRef, useState } from 'react';
import TermsModal from './components/ui/TermsModal';
import "@/App.css";
import axios from "axios";
import { ThemeProvider, useTheme } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { Moon, Sun, Boxes, AlertTriangle, ShieldCheck, Info, ScrollText, Download, LogOut } from "lucide-react";
import { UploadZone } from "@/components/amigurumi/UploadZone";
import { ConfigPanel } from "@/components/amigurumi/ConfigPanel";
import { ComponentCard } from "@/components/amigurumi/ComponentCard";
import { ConfidenceSummary } from "@/components/amigurumi/ConfidenceSummary";
import { CodePanel } from "@/components/amigurumi/CodePanel";
import { buildLatexDocument } from "@/lib/latex";
import { isSupabaseConfigured, supabase } from "@/supabaseClient";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
// An empty value intentionally falls back to same-origin `/api`, which is the
// usual production setup when the frontend and API share a domain/proxy.
const API = `${BACKEND_URL}/api`;

const overallLabelStyles = {
  high: "text-emerald-600 dark:text-emerald-400",
  medium: "text-amber-600 dark:text-amber-400",
  low: "text-red-600 dark:text-red-400",
};

const errorMessage = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail.message === "string") return detail.message;
  return fallback;
};

const ThemeToggle = () => {
  const { theme, setTheme, resolvedTheme } = useTheme();
  const current = theme === "system" ? resolvedTheme : theme;
  return (
    <button
      data-testid="theme-toggle"
      onClick={() => setTheme(current === "dark" ? "light" : "dark")}
      className="inline-flex items-center justify-center h-9 w-9 rounded-md border border-zinc-200 dark:border-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
      aria-label="Toggle theme"
    >
      <Sun className="h-4 w-4 hidden dark:block" />
      <Moon className="h-4 w-4 dark:hidden" />
    </button>
  );
};

const Scaler = ({ accessToken, onSignOut }) => {
  const [images, setImages] = useState([]);
  const [mode, setMode] = useState("amigurumi");
  const [anchor, setAnchor] = useState("42");
  const [multiplier, setMultiplier] = useState("1.0");
  const [startingStitches, setStartingStitches] = useState("6");
  const [targetSize, setTargetSize] = useState("M");
  const [gaugeStitches, setGaugeStitches] = useState("16");
  const [gaugeRows, setGaugeRows] = useState("20");
  const [mainStitchPattern, setMainStitchPattern] = useState("double crochet");
  const [modelName, setModelName] = useState("gemini-2.5-flash");
  const [globalNotes, setGlobalNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [refiningComponents, setRefiningComponents] = useState({});
  const [validationPending, setValidationPending] = useState(false);
  const validationTimer = useRef(null);
  const validationRevision = useRef(0);
  const authorizedRequest = {
    headers: { Authorization: `Bearer ${accessToken}` },
  };

  useEffect(() => () => {
    if (validationTimer.current) clearTimeout(validationTimer.current);
  }, []);

  const generate = async () => {
    if (images.length === 0) {
      toast.error("Please upload at least one image.");
      return;
    }

    const payload = {
      images: images.map((i) => i.dataUrl),
      mode: mode,
      model_name: modelName,
      global_notes: globalNotes,
    };

    if (mode === "amigurumi") {
      const anchorNum = parseInt(anchor, 10);
      const multNum = parseFloat(multiplier);
      const startStitchesNum = parseInt(startingStitches, 10);

      if (!anchorNum || anchorNum < 1) {
        toast.error("Enter a valid Max Stitch Count (Anchor).");
        return;
      }
      if (!multNum || multNum <= 0) {
        toast.error("Enter a valid Scale Multiplier greater than 0.");
        return;
      }
      if (!startStitchesNum || startStitchesNum < 1) {
        toast.error("Enter a valid Starting Stitches count.");
        return;
      }

      payload.anchor_max_stitches = anchorNum;
      payload.scale_multiplier = multNum;
      payload.starting_stitches = startStitchesNum;
    } else {
      const stitchesNum = parseInt(gaugeStitches, 10);
      const rowsNum = parseInt(gaugeRows, 10);

      if (!stitchesNum || stitchesNum < 1) {
        toast.error("Enter a valid gauge stitch count.");
        return;
      }
      if (!rowsNum || rowsNum < 1) {
        toast.error("Enter a valid gauge row count.");
        return;
      }

      payload.target_size = targetSize;
      payload.gauge_stitches = stitchesNum;
      payload.gauge_rows = rowsNum;
      payload.main_stitch_pattern = mainStitchPattern;
    }

    setLoading(true);
    setResult(null);
    setGenerationError(null);
    validationRevision.current += 1;
    if (validationTimer.current) clearTimeout(validationTimer.current);
    setValidationPending(false);
    try {
      const { data } = await axios.post(`${API}/generate-pattern`, payload, authorizedRequest);
      setResult(data);
      toast.success("Analysis complete");
    } catch (err) {
      const message = errorMessage(err, "Something went wrong during analysis.");
      const detail = err?.response?.data?.detail;
      setGenerationError({
        status: err?.response?.status,
        message,
        details: Array.isArray(detail?.errors) ? detail.errors : [],
      });
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const runValidation = async (updatedJson, revision) => {
    try {
      const { data } = await axios.post(`${API}/validate`, { data: updatedJson }, authorizedRequest);
      if (revision !== validationRevision.current) return;
      setResult(prev => ({
        ...prev,
        validation: data.validation,
        latex: data.latex
      }));
    } catch (err) {
      console.error("Validation failed", err);
      if (revision === validationRevision.current) {
        toast.error("Could not revalidate the edit. Check that the backend is running.");
      }
    } finally {
      if (revision === validationRevision.current) setValidationPending(false);
    }
  };

  const queueValidation = (updatedJson, delay = 0) => {
    const revision = ++validationRevision.current;
    if (validationTimer.current) clearTimeout(validationTimer.current);
    setValidationPending(true);
    validationTimer.current = setTimeout(() => {
      validationTimer.current = null;
      runValidation(updatedJson, revision);
    }, delay);
  };

  const handleUpdateRound = (componentId, roundNumber, field, newValue) => {
    if (!result) return;
    
    const updatedJson = JSON.parse(JSON.stringify(result.raw_json));
    const comp = updatedJson.components.find(c => c.component_id === componentId);
    if (comp) {
      const rnd = comp.rounds.find(r => r.round_number === roundNumber);
      if (rnd) {
        if (field === "total_count" || field === "starting_chain_count") {
          rnd[field] = parseInt(newValue, 10) || 0;
          if (field === "starting_chain_count" && rnd[field] === 0) {
            rnd.starting_chain_counts_as_stitch = false;
          }
        } else {
          rnd[field] = newValue;
        }
        if (field === "work_style" && newValue !== "joined" && rnd.join_method === "slst") {
          rnd.join_method = "none";
        }
        if (field === "join_method" && newValue === "slst") {
          rnd.work_style = "joined";
        }
      }
    }
    
    setResult(prev => ({
      ...prev,
      raw_json: updatedJson
    }));
    
    queueValidation(updatedJson, 400);
  };

  const handleAddRound = (componentId) => {
    if (!result) return;
    
    const updatedJson = JSON.parse(JSON.stringify(result.raw_json));
    const comp = updatedJson.components.find(c => c.component_id === componentId);
    if (comp) {
      if (!comp.rounds) comp.rounds = [];
      const nextRoundNumber = comp.rounds.length + 1;
      const lastRound = comp.rounds[comp.rounds.length - 1];
      comp.rounds.push({
        round_number: nextRoundNumber,
        work_style: mode === "garment" ? "flat" : "continuous",
        starting_chain_count: 0,
        starting_chain_counts_as_stitch: false,
        join_method: "none",
        stitch_sequence: "sc around",
        total_count: lastRound ? lastRound.total_count : 6,
        beginner_instruction: "",
      });
      comp.estimated_row_count = comp.rounds.length;
    }
    
    setResult(prev => ({
      ...prev,
      raw_json: updatedJson
    }));
    
    queueValidation(updatedJson);
  };

  const handleDeleteRound = (componentId, roundNumber) => {
    if (!result) return;
    
    const updatedJson = JSON.parse(JSON.stringify(result.raw_json));
    const comp = updatedJson.components.find(c => c.component_id === componentId);
    if (comp && comp.rounds) {
      comp.rounds = comp.rounds.filter(r => r.round_number !== roundNumber);
      comp.rounds = comp.rounds.map((r, idx) => ({
        ...r,
        round_number: idx + 1
      }));
      comp.estimated_row_count = comp.rounds.length;
    }
    
    setResult(prev => ({
      ...prev,
      raw_json: updatedJson
    }));
    
    queueValidation(updatedJson);
  };


  const handleRefineComponent = async (componentId, instructions) => {
    if (!result) return;
    
    const currentComp = result.raw_json.components.find(c => c.component_id === componentId);
    if (!currentComp) return;
    
    const otherCompsSummary = result.raw_json.components
      .filter(c => c.component_id !== componentId)
      .map(c => ({
        component_name: c.component_name,
        estimated_row_count: c.estimated_row_count || c.rounds?.length || 0,
        confidence_label: c.confidence_label || "unknown"
      }));

    const payload = {
      images: images.map((i) => i.dataUrl),
      mode: mode,
      model_name: modelName,
      component_to_refine: currentComp,
      other_components_summary: otherCompsSummary,
      refinement_instructions: instructions,
      global_notes: globalNotes,
    };

    if (mode === "amigurumi") {
      payload.anchor_max_stitches = parseInt(anchor, 10);
      payload.scale_multiplier = parseFloat(multiplier);
      payload.starting_stitches = parseInt(startingStitches, 10);
    } else {
      payload.target_size = targetSize;
      payload.gauge_stitches = parseInt(gaugeStitches, 10);
      payload.gauge_rows = parseInt(gaugeRows, 10);
      payload.main_stitch_pattern = mainStitchPattern;
    }

    setRefiningComponents(prev => ({ ...prev, [componentId]: true }));
    
    try {
      const { data } = await axios.post(`${API}/refine-component`, payload, authorizedRequest);
      validationRevision.current += 1;
      if (validationTimer.current) clearTimeout(validationTimer.current);
      setValidationPending(false);
      
      const updatedJson = JSON.parse(JSON.stringify(result.raw_json));
      const compIdx = updatedJson.components.findIndex(c => c.component_id === componentId);
      if (compIdx !== -1) {
        updatedJson.components[compIdx] = data.refined_component;
      }
      
      setResult(prev => {
        const nextValidation = { ...prev.validation };
        nextValidation.components = {
          ...nextValidation.components,
          [componentId]: data.validation
        };
        nextValidation.is_valid = (nextValidation.top_level_errors || []).length === 0
          && Object.values(nextValidation.components).every(component => component.is_valid);
        
        let nextLatex = [...(prev.latex || [])];
        if (data.latex) {
          const idx = nextLatex.findIndex(l => l && l.component_id === componentId);
          if (idx !== -1) {
            nextLatex[idx] = data.latex;
          } else {
            nextLatex.push(data.latex);
          }
        } else {
          nextLatex = nextLatex.filter(l => l && l.component_id !== componentId);
        }
        nextLatex = nextLatex.filter(Boolean);
        
        return {
          raw_json: updatedJson,
          validation: nextValidation,
          latex: nextLatex
        };
      });
      
      toast.success(`${currentComp.component_name} refined successfully!`);
    } catch (err) {
      toast.error(errorMessage(err, "Refinement failed."));
    } finally {
      setRefiningComponents(prev => ({ ...prev, [componentId]: false }));
    }
  };



  const raw = result?.raw_json;
  const compValidation = result?.validation?.components || {};
  const latexBlocks = result?.latex || [];
  const visualAnalysis = raw?.visual_analysis;
  const visualNames = Object.fromEntries(
    (visualAnalysis?.components || []).map((component) => [component.component_id, component.component_name])
  );
  const latexDocument = buildLatexDocument({
    blocks: latexBlocks,
    mode,
    anchor,
    multiplier,
    targetSize,
    gaugeStitches,
    gaugeRows,
    mainStitchPattern,
  });
  const overallLabel = (raw?.overall_confidence_label || "low").toLowerCase();

  const downloadTex = () => {
    const blob = new Blob([latexDocument], { type: "text/x-tex" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = mode === "garment" ? "garment-pattern.tex" : "amigurumi-pattern.tex";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${a.download}`);
  };

  return (
    <div className="App min-h-screen bg-zinc-50 dark:bg-[#09090B] text-zinc-900 dark:text-zinc-50">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/80 dark:bg-[#09090B]/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-zinc-900 dark:bg-zinc-50 flex items-center justify-center">
              <Boxes className="h-5 w-5 text-zinc-50 dark:text-zinc-900" />
            </div>
            <div>
              <h1 className="font-display font-bold text-lg sm:text-xl tracking-tight leading-none">
                AmiGuru
              </h1>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                AI-assisted crochet drafting · toys & garments
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onSignOut}
              className="inline-flex items-center gap-1.5 h-9 rounded-md border border-zinc-200 dark:border-zinc-800 px-3 text-xs font-medium text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-4 flex flex-col gap-6">
            <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-5">
              <h2 className="font-display font-semibold text-base mb-4">1 · Reference Images</h2>
              <UploadZone images={images} setImages={setImages} />
            </section>

            <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-5">
              <h2 className="font-display font-semibold text-base mb-4">2 · Parameters</h2>
              <ConfigPanel
                mode={mode}
                setMode={setMode}
                anchor={anchor}
                setAnchor={setAnchor}
                multiplier={multiplier}
                setMultiplier={setMultiplier}
                startingStitches={startingStitches}
                setStartingStitches={setStartingStitches}
                targetSize={targetSize}
                setTargetSize={setTargetSize}
                gaugeStitches={gaugeStitches}
                setGaugeStitches={setGaugeStitches}
                gaugeRows={gaugeRows}
                setGaugeRows={setGaugeRows}
                mainStitchPattern={mainStitchPattern}
                setMainStitchPattern={setMainStitchPattern}
                modelName={modelName}
                setModelName={setModelName}
                globalNotes={globalNotes}
                setGlobalNotes={setGlobalNotes}
                onGenerate={generate}
                loading={loading}
                canGenerate={images.length > 0}
              />
            </section>

            <div className="flex items-start gap-2 text-xs text-zinc-400 px-1">
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                This is a drafting aid. Only sufficiently confident components receive generated patterns; everything
                else is flagged for manual refinement.
              </span>
            </div>
          </aside>

          {/* Main results */}
          <div className="lg:col-span-8 flex flex-col gap-6">
            {!result && !loading && (
              <div className="rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700 bg-white/50 dark:bg-zinc-900/30 flex flex-col items-center justify-center text-center py-20 px-6">
                <ScrollText className="h-8 w-8 text-zinc-300 dark:text-zinc-600 mb-3" strokeWidth={1.5} />
                <p className="font-display font-medium text-zinc-600 dark:text-zinc-300">No analysis yet</p>
                <p className="text-sm text-zinc-400 mt-1 max-w-sm">
                  {mode === "garment"
                    ? "Upload 1–6 photos of your garment (multiple angles recommended), set your size and gauge, then generate."
                    : "Upload 1–6 photos of your amigurumi (multiple angles recommended), set your anchor and scale, then generate."
                  }
                </p>
              </div>
            )}

            {generationError && !loading && !result && (
              <div
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 dark:border-red-900/60 dark:bg-red-950/30 px-4 py-4"
                data-testid="generation-error"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                  Generation stopped{generationError.status ? ` · HTTP ${generationError.status}` : ""}
                </p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-300">{generationError.message}</p>
                {generationError.details.length > 0 && (
                  <ul className="mt-2 list-disc list-inside space-y-0.5 text-xs text-red-600 dark:text-red-400">
                    {generationError.details.map((detail, index) => (
                      <li key={`generation-error-${index}`}>{detail}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {loading && (
              <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 py-20 flex flex-col items-center justify-center">
                <div className="h-8 w-8 rounded-full border-2 border-zinc-300 dark:border-zinc-700 border-t-zinc-900 dark:border-t-zinc-50 animate-spin" />
                <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-4">
                  {mode === "garment"
                    ? "Vision AI is drafting your garment panels…"
                    : "Scanning proportions, then drafting and validating the parts…"}
                </p>
              </div>
            )}

            {result && raw && (
              <>
                {/* Overall summary */}
                <section className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-5" data-testid="summary-panel">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-zinc-400 mb-1">Object Summary</p>
                      <p className="text-sm leading-relaxed text-zinc-700 dark:text-zinc-200">{raw.object_summary}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs font-semibold uppercase tracking-[0.06em] text-zinc-400 mb-1">Overall Confidence</p>
                      <p className={`font-display font-bold text-2xl ${overallLabelStyles[overallLabel]}`}>
                        {Number(raw.overall_confidence_score).toFixed(2)}
                      </p>
                      <p className={`text-xs font-semibold uppercase ${overallLabelStyles[overallLabel]}`}>{overallLabel}</p>
                    </div>
                  </div>

                  {overallLabel === "low" && (
                    <div className="mt-4 flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30 px-3.5 py-3" data-testid="low-confidence-warning">
                      <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                      <p className="text-xs leading-relaxed text-red-700 dark:text-red-300">
                        Overall confidence is low. Treat all outputs as rough drafts requiring manual refinement.
                      </p>
                    </div>
                  )}

                  {(raw.image_quality_notes?.length > 0 || raw.global_assumptions?.length > 0 || raw.ambiguities?.length > 0) && (
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                      {[
                        { k: "Image Quality", items: raw.image_quality_notes },
                        { k: "Global Assumptions", items: raw.global_assumptions },
                        { k: "Ambiguities", items: raw.ambiguities },
                      ].map((col) => (
                        <div key={col.k}>
                          <p className="font-semibold uppercase tracking-wide text-zinc-400 mb-1.5">{col.k}</p>
                          {col.items?.length ? (
                            <ul className="list-disc list-inside space-y-0.5 text-zinc-500 dark:text-zinc-400">
                              {col.items.map((it, i) => <li key={`${col.k}-${i}`}>{it}</li>)}
                            </ul>
                          ) : (
                            <p className="text-zinc-400">—</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                {visualAnalysis && (
                  <section
                    className="rounded-xl border border-sky-200 dark:border-sky-900/60 bg-sky-50/60 dark:bg-sky-950/20 p-5"
                    data-testid="visual-expectations"
                  >
                    <div className="flex items-start gap-2.5">
                      <ShieldCheck className="h-4 w-4 text-sky-600 dark:text-sky-400 mt-0.5 shrink-0" />
                      <div className="flex-1">
                        <h2 className="font-display font-semibold text-base">Visual baseline</h2>
                        <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                          These observations were recorded before pattern drafting and checked again before display.
                        </p>
                      </div>
                    </div>

                    {visualAnalysis.components?.length > 0 && (
                      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {visualAnalysis.components.map((component) => (
                          <div key={`visual-${component.component_id}`} className="rounded-lg border border-sky-100 dark:border-sky-900/40 bg-white/70 dark:bg-zinc-950/30 px-3 py-2 text-xs">
                            <p className="font-semibold text-zinc-700 dark:text-zinc-200">{component.component_name}</p>
                            <p className="text-zinc-500 dark:text-zinc-400 mt-0.5">
                              {component.estimated_round_count != null
                                ? `about ${component.estimated_round_count} rounds (±${component.round_count_tolerance})`
                                : "round count uncertain"}
                              {component.estimated_max_stitches != null
                                ? ` · max near ${component.estimated_max_stitches} stitches (±${component.max_stitches_tolerance})`
                                : ""}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {visualAnalysis.relationships?.length > 0 && (
                      <ul className="mt-3 list-disc list-inside space-y-1 text-xs text-zinc-600 dark:text-zinc-300">
                        {visualAnalysis.relationships.map((relationship, index) => {
                          const subject = visualNames[relationship.subject_component_id] || relationship.subject_component_id;
                          const reference = visualNames[relationship.reference_component_id] || relationship.reference_component_id;
                          const facts = [];
                          if (relationship.width_relation !== "unknown") facts.push(`${relationship.width_relation} width`);
                          if (relationship.height_relation !== "unknown") facts.push(`${relationship.height_relation}`);
                          if (relationship.estimated_round_difference != null) {
                            const difference = relationship.estimated_round_difference;
                            facts.push(`${difference >= 0 ? "+" : ""}${difference} rounds (±${relationship.round_tolerance})`);
                          }
                          return <li key={`relationship-${index}`}>{subject} vs. {reference}: {facts.join(" · ")}</li>;
                        })}
                      </ul>
                    )}
                  </section>
                )}

                {/* Component analysis */}
                <section data-testid="component-analysis">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display font-semibold text-lg">Component Analysis</h2>
                    <span className="text-xs text-zinc-400">{raw.components?.length || 0} detected</span>
                  </div>
                  {raw.components?.length > 0 && (
                    <div className="mb-4">
                      <ConfidenceSummary components={raw.components} compValidation={compValidation} />
                    </div>
                  )}
                  {raw.components?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {raw.components.map((c) => (
                        <ComponentCard
                          key={c.component_id}
                          comp={c}
                          validation={compValidation[c.component_id]}
                          onUpdateRound={handleUpdateRound}
                          onAddRound={handleAddRound}
                          onDeleteRound={handleDeleteRound}
                          onRefine={handleRefineComponent}
                          isRefining={refiningComponents[c.component_id]}
                          mode={raw?.mode || mode}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30 px-4 py-4 text-sm text-amber-800 dark:text-amber-300">
                      No components could be reliably extracted. See image-quality notes and ambiguities above.
                    </div>
                  )}
                </section>

                {/* LaTeX output */}
                <section>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-emerald-500" />
                      <h2 className="font-display font-semibold text-lg">Validated LaTeX Rows</h2>
                      {validationPending && (
                        <span className="text-xs text-zinc-400" role="status">Revalidating edits…</span>
                      )}
                    </div>
                    {latexBlocks.length > 0 && (
                      <button
                        data-testid="export-tex-button"
                        onClick={downloadTex}
                        className="inline-flex items-center gap-1.5 rounded-md border border-zinc-200 dark:border-zinc-700 px-3 py-1.5 text-xs font-medium text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                      >
                        <Download className="h-3.5 w-3.5" /> Export .tex
                      </button>
                    )}
                  </div>
                  {latexBlocks.length > 0 ? (
                    <div className="flex flex-col gap-4">
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">
                        Export creates a complete, standalone LaTeX document with a safety notice and formatted tables.
                      </p>
                      {latexBlocks.map((b) => (
                        <div key={b.component_id}>
                          <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300 mb-1.5">{b.component_name}</p>
                          <CodePanel title="LaTeX" testId={`latex-${b.component_id}`} content={b.latex} />
                        </div>
                      ))}
                      <div>
                        <p className="text-sm font-medium text-zinc-600 dark:text-zinc-300 mb-1.5">Complete export document</p>
                        <CodePanel title="LaTeX document" testId="latex-all" content={latexDocument} />
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 px-4 py-4 text-sm text-zinc-500 dark:text-zinc-400">
                      No components passed stitch-math validation, so no LaTeX rows were produced. Accuracy is
                      prioritized over completeness.
                    </div>
                  )}
                </section>

                {/* Raw JSON is diagnostic data, not a validated pattern surface. */}
                <details className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900/50 p-4">
                  <summary className="font-display font-semibold text-sm cursor-pointer text-zinc-600 dark:text-zinc-300">
                    Diagnostic JSON
                  </summary>
                  <div className="mt-3">
                    <CodePanel title="JSON" testId="raw-json" content={JSON.stringify(raw, null, 2)} />
                  </div>
                </details>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

const AccessScreen = ({ kicker, title, children, action }) => (
  <div className="min-h-screen bg-zinc-950 text-zinc-50 flex items-center justify-center px-5 py-12">
    <main className="w-full max-w-xl rounded-2xl border border-zinc-800 bg-zinc-900/70 p-7 sm:p-10 shadow-2xl">
      <div className="h-11 w-11 rounded-xl bg-emerald-400 text-zinc-950 flex items-center justify-center mb-7">
        <Boxes className="h-6 w-6" />
      </div>
      <p className="font-mono text-xs uppercase tracking-[0.16em] text-emerald-400">{kicker}</p>
      <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-3">{title}</h1>
      <div className="mt-5 text-sm leading-7 text-zinc-300">{children}</div>
      {action && <div className="mt-8">{action}</div>}
    </main>
  </div>
);

function AmiGuruGate() {
  const [isTermsOpen, setIsTermsOpen] = useState(true);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [accessState, setAccessState] = useState("idle");
  const [accessMessage, setAccessMessage] = useState("");

  useEffect(() => {
    if (!supabase) {
      setAuthLoading(false);
      return undefined;
    }

    let isCurrent = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      if (!isCurrent) return;
      setSession(nextSession);
      setAuthLoading(false);
    });

    supabase.auth.getSession().then(({ data }) => {
      if (!isCurrent) return;
      setSession(data.session);
      setAuthLoading(false);
    });

    return () => {
      isCurrent = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) {
      setAccessState("idle");
      setAccessMessage("");
      return undefined;
    }

    let isCurrent = true;
    setAccessState("checking");
    axios.get(`${API}/access`, {
      headers: { Authorization: `Bearer ${session.access_token}` },
    }).then(() => {
      if (isCurrent) setAccessState("allowed");
    }).catch((error) => {
      if (!isCurrent) return;
      setAccessState(error?.response?.status === 403 ? "preview" : "error");
      setAccessMessage(errorMessage(error, "AmiGuru access could not be checked."));
    });

    return () => {
      isCurrent = false;
    };
  }, [session]);

  const signIn = async () => {
    setAccessMessage("");
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: window.location.origin },
    });
    if (error) setAccessMessage("Google sign-in could not be started.");
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
  };

  if (!isSupabaseConfigured) {
    return (
      <AccessScreen kicker="Access gate offline" title="AmiGuru is not configured for sign-in.">
        The working build is closed until its Supabase connection is configured.
      </AccessScreen>
    );
  }

  if (authLoading || (session && ["idle", "checking"].includes(accessState))) {
    return (
      <AccessScreen kicker="Checking workshop clearance" title="Opening AmiGuru…">
        Verifying your signed-in account and project access.
      </AccessScreen>
    );
  }

  if (!session) {
    return (
      <AccessScreen
        kicker="Selected tester build"
        title="Sign in before entering the workshop."
        action={(
          <button
            type="button"
            onClick={signIn}
            className="w-full rounded-lg bg-emerald-400 px-5 py-3 text-sm font-semibold text-zinc-950 hover:bg-emerald-300 transition-colors"
          >
            Continue with Google
          </button>
        )}
      >
        <p>
          The public story lives on Beck&apos;s Brainstorms. AmiGuru&apos;s working tools are available only
          to accounts Beck has deliberately added to the tester list.
        </p>
        {accessMessage && <p className="mt-3 text-red-300" role="alert">{accessMessage}</p>}
      </AccessScreen>
    );
  }

  if (accessState === "preview" || accessState === "error") {
    return (
      <AccessScreen
        kicker={accessState === "preview" ? "Signed-in preview" : "Access check interrupted"}
        title={accessState === "preview" ? "The tools are still locked for this account." : "AmiGuru could not verify access."}
        action={(
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              className="flex-1 rounded-lg bg-emerald-400 px-5 py-3 text-center text-sm font-semibold text-zinc-950 hover:bg-emerald-300 transition-colors"
              href="https://becksbrainstorms.com/work/amiguru"
            >
              Return to the preview
            </a>
            <button
              type="button"
              onClick={signOut}
              className="rounded-lg border border-zinc-700 px-5 py-3 text-sm font-semibold hover:bg-zinc-800 transition-colors"
            >
              Sign out
            </button>
          </div>
        )}
      >
        <p>{accessMessage}</p>
        {accessState === "preview" && (
          <p className="mt-3">Beck chooses working-build testers individually while AmiGuru is still taking shape.</p>
        )}
      </AccessScreen>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <TermsModal
        isOpen={isTermsOpen}
        onAccept={() => setIsTermsOpen(false)}
      />
      <Scaler accessToken={session.access_token} onSignOut={signOut} />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AmiGuruGate />
    </ThemeProvider>
  );
}

export default App;
